import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { MemberInfo } from "../../../types/member";

import { ERROR_CODE } from "../../../consts/code";
import { VOTE_STATUS } from "../../../consts/hotColdVote";

import { getDateString, isDateTimePassed } from "../../../libs/dateFormat";

import { useUserContext } from "../../../contexts/userContext";

import {
  HotColdVoteListRequest,
  Vote,
  VoteSpace,
  getHotColdVoteList,
  getHotColdVoteStatus,
  isWeekendOrAfter6PM,
} from "../../../services/hotColdVoteServices";
import { getHolidayCheck } from "../../../services/etcServices";
import { getMemberInfo } from "../../../services/memberServices";
import { logout } from "../../../services/authServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../hooks/useInfiniteScroll";
import { RefreshIcon } from "../../../hooks/useStoreHeader";

import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonSelect, { SelectOption } from "../../../components/Select";
import BottomButtonGroup from "../../../components/Button/Bottom";
import HotColdBadge from "../../../components/Badge/HotColdBadge";
import CommonPopup from "../../../components/Popup";
import CommonButton from "../../../components/Button";
import HotColdVoteReportPopup from "../../../components/Popup/HotColdVoteReport";
import HotColdVoteOnGoingPopup from "../../../components/Popup/HotColdVoteOnGoing";
import HotColdVoteEndPopup from "../../../components/Popup/HotColdVoteEnd";
import { HotColdVoteBarChart } from "../../../components/HotCold/BarChart";
import { HotColdVoteRemainTime } from "../../../components/HotCold/RemainTime";
import { Skeleton } from "../../Talk";

import { PostListNoPostIcon } from "../../../components/Talk/PostList";

type Props = {};

export default function HotColdVote(props: Props) {
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;

  const navigate = useNavigate();

  const [user, { updateField }] = useUserContext();
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [isMemberLoading, setMemberIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isListLoading || isMemberLoading,
  });

  const [memberInfo, setMemberInfo] = useState<MemberInfo>();
  const [voteSpace, setVoteSpace] = useState<VoteSpace>();
  const [voteList, setVoteList] = useState<Vote[]>([]);
  const [popup, setPopup] = useState<
    "report" | "voteEnd" | "onGoing" | "nullSpace" | ""
  >("");

  const filterList: SelectOption[] = [
    { value: "all", label: "전체보기" },
    { value: "mySpace", label: "내 위치 투표 우선" },
    { value: "onlyMyVote", label: "내가 참여한 투표" },
    { value: "onGoing", label: "진행중" },
    { value: "completed", label: "종료" },
  ];

  const [selectedFilter, setSelectedFilter] = useState<SelectOption>(
    filterList[1]
  );

  const getFilterParams = (): Partial<HotColdVoteListRequest> => {
    switch (selectedFilter?.value) {
      case "mySpace":
        return { sortOrder: "space" };
      case "onlyMyVote":
        return { onlyMyVote: true };
      case "onGoing":
        return { status: VOTE_STATUS.ONGOING };
      case "completed":
        return { status: VOTE_STATUS.COMPLETED };
      default:
        return {};
    }
  };

  const loadHotColdList = async (
    args?: InfiniteScrollCallbackArgs,
    voteDate: string = getDateString(new Date())
  ) => {
    let itemLength = INFINITE_CODE.ERROR;
    try {
      setIsListLoading(true);

      const res = await getHotColdVoteList({
        ...args,
        buildingId: memberInfo?.buildingId,
        voteDate,
        ...getFilterParams(),
      });
      if (res.status === HttpStatusCode.Ok) {
        if (args?.page === 1) {
          setVoteList(res.data.items);
        } else {
          setVoteList((prev) => [...prev, ...res.data.items]);
        }
        itemLength = res.data.items?.length ?? 0;
      }
    } catch (e: any) {
      if (
        [
          ERROR_CODE.COMPANY_NOT_CERTIFIED,
          ERROR_CODE.COMPANY_NOT_IN_BUILDING,
          ERROR_CODE.SERVICE_DISABLED,
        ].includes(e?.response?.data?.code) &&
        e?.response?.data?.error
      ) {
        open?.(e?.response?.data?.error.replace(". ", ".\n"), {
          onClick: () => navigate("/view"),
          enableDimmedClose: false,
        });
      } else if (e?.response?.data?.message) {
        open?.(e?.response?.data?.message.replace(". ", ".\n"));
      } else {
        open?.("목록 조회에 실패했습니다.\n관리자에게 문의해주세요.");
      }
    } finally {
      setIsListLoading(false);
    }

    return itemLength;
  };

  const loadMemberInfo = async () => {
    try {
      const res = await getMemberInfo();
      if (res.status === HttpStatusCode.Ok) {
        setMemberInfo(res.data);
        updateField({ ...res.data });
      } else {
        console.log("MyInfo getMemberInfo status", res.status);
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open?.(e?.response?.data?.message.replace(". ", ".\n"));
      } else {
        open?.("내정보 조회에 실패하였습니다.");
      }
    }
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadHotColdList,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  const handleRefresh = () => {
    resetScroll();
    loadHotColdList({ page: SCROLL_LOAD_START_PAGE - 1, pageSize: PAGE_SIZE });
  };

  useEffect(() => {
    if (!memberInfo?.buildingId) return;

    if (!user.buildingSpaceId) {
      setPopup("nullSpace");
    }

    loadHotColdList({
      page: SCROLL_LOAD_START_PAGE - 1,
      pageSize: PAGE_SIZE,
    });
  }, [memberInfo]);

  useEffect(() => {
    if (!selectedFilter || !memberInfo) return;

    handleRefresh();
  }, [selectedFilter]);

  useEffect(() => {
    resetScroll();

    setIsLoading(true);
    setMemberIsLoading(true);
    loadMemberInfo().finally(() => {
      setIsLoading(false);
      setMemberIsLoading(false);
    });
  }, []);

  return (
    <div className="flex flex-col pb-10">
      {loadingComponent}
      <StickyHeader
        title="더워요 추워요"
        className="px-5"
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />
      <div className="flex flex-col gap-1 px-5 pb-2.5">
        {isMemberLoading ? (
          <Skeleton className="h-[35px] w-[200px] rounded-lg" />
        ) : (
          <>
            <div className="h-[35px] text-[26px] leading-[38px] font-medium">
              {user.buildingName ?? ""}
            </div>
          </>
        )}

        {isMemberLoading ? (
          <Skeleton className="h-[22px] w-[200px] rounded-lg" />
        ) : (
          <span className="h-[22px] text-sm  leading-[18px]">
            {`내 위치 : ${user?.buildingFloorName ?? ""} ${
              user?.buildingSpaceName ?? ""
            }`}
          </span>
        )}

        <CommonButton
          variant="tertiary-s"
          className="w-fit"
          onClick={() => {
            navigate("/myinfo/change/building", { state: { goBack: true } });
          }}
        >
          건물 위치 변경
        </CommonButton>
      </div>
      <HorizontalRule
        thickness={6}
        className="bg-brand-neutral-05"
        margin="10px"
      />
      <div className="flex justify-between items-center px-5 pb-2.5">
        <span className="font-medium text-lg">오늘 하루 투표</span>
        <CommonSelect
          value={selectedFilter.value}
          optionList={filterList}
          onChange={(option) => {
            setSelectedFilter(option);
          }}
          className="border-none max-w-[200px] text-wrap text-right cursor-pointer"
          wrapperClassName="flex justify-end"
          optionWidth="200px"
          optionHeight="200px"
          optionOrder="right"
          variant="transparent"
        />
      </div>
      <div className="px-5">
        <div className="flex flex-col gap-5">
          {isListLoading ? (
            <>
              {Array.from({ length: 3 }, (_, index) => index).map(
                (value, index) => {
                  return (
                    <Skeleton
                      key={index}
                      className="w-full h-[177px] rounded-lg"
                    />
                  );
                }
              )}
            </>
          ) : (
            <>
              {voteList && voteList.length > 0 ? (
                voteList.map((vote, index) => {
                  return (
                    <div
                      key={`${vote.voteId}${index}`}
                      className="flex flex-col rounded-[10px] border border-brand-neutral-15 cursor-pointer"
                      onClick={() => {
                        if (!user.buildingSpaceId) {
                          setPopup("nullSpace");
                          return;
                        }

                        const params = new URLSearchParams();
                        vote.voteId &&
                          params.append("voteId", vote.voteId?.toString());
                        params.append("buildingFloorId", vote.buildingFloorId);
                        params.append("floorName", vote.buildingFloorName);
                        params.append("buildingSpaceId", vote.buildingSpaceId);
                        params.append("spaceName", vote.buildingSpaceName);

                        navigate(
                          `/service/hot-cold-vote/detail?${params?.toString()}`
                        );
                      }}
                    >
                      <div className="flex justify-between items-center p-5 gap-1">
                        <div className="flex flex-col">
                          <div className="flex flex-col pb-[14px]">
                            <span className="font-semibold text-[20px] leading-[24px]">{`${vote.buildingFloorName} ${vote.buildingSpaceName}`}</span>
                            {vote.location && (
                              <span className="text-brand-neutral-50 text-sm leading-[18px]">
                                {vote.location}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-[6px]">
                            <HotColdBadge
                              children={
                                vote.votePeriod === "AM"
                                  ? "오전투표"
                                  : "오후투표"
                              }
                            />
                            <HotColdBadge
                              children={`참여인원 : ${
                                vote.summary.coldCount +
                                vote.summary.goodCount +
                                vote.summary.hotCount
                              }명`}
                            />
                          </div>
                        </div>
                        <HotColdStatusBadge vote={vote} />
                      </div>
                      <HorizontalRule
                        margin="0"
                        className="bg-brand-neutral-15"
                      />
                      <div className="p-5">
                        <HotColdVoteBarChart voteSummary={vote.summary} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
                  <PostListNoPostIcon />
                  조회 결과가 없어요
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <BottomButtonGroup
        title="투표 만들기"
        onClick={() => {
          if (!user.buildingSpaceId) {
            setPopup("nullSpace");
            return;
          }

          setIsLoading(true);
          Promise.allSettled([
            getHotColdVoteStatus({ buildingSpaceId: user.buildingSpaceId }),
            getHolidayCheck({ date: getDateString(new Date()) }),
          ])
            .then((ress) => {
              if (
                ress[0].status === "fulfilled" &&
                ress[0].value.status === HttpStatusCode.Ok
              ) {
                const res = ress[0].value;
                setVoteSpace(res.data);
                if (res.status === HttpStatusCode.Ok) {
                  if (res.data?.vote?.status === VOTE_STATUS.ONGOING) {
                    setPopup("onGoing");
                    return;
                  } else if (res.data?.vote?.status === VOTE_STATUS.COMPLETED) {
                    setPopup("voteEnd");
                    return;
                  }
                }
              }

              if (
                ress[1].status === "fulfilled" &&
                ress[1].value.status === HttpStatusCode.Ok
              ) {
                if (ress[1].value.data?.isHoliday || isWeekendOrAfter6PM()) {
                  setPopup("report");
                  return;
                }
              }
              const params = new URLSearchParams();

              user.buildingId && params.append("buildingId", user.buildingId);
              user.buildingName &&
                params.append("buildingName", user.buildingName);
              user.buildingFloorId &&
                params.append("buildingFloorId", user.buildingFloorId);
              user.buildingFloorName &&
                params.append("floorName", user.buildingFloorName);
              user.buildingSpaceId &&
                params.append("buildingSpaceId", user.buildingSpaceId);
              user.buildingSpaceName &&
                params.append("spaceName", user.buildingSpaceName);

              navigate(`/service/hot-cold-vote/detail?${params?.toString()}`);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      />

      <HotColdVoteEndPopup
        popup={popup}
        onClose={() => setPopup("")}
        onConfirm={() => {
          if (
            !user.buildingId ||
            !user.buildingFloorId ||
            !user.buildingFloorName ||
            !user.buildingSpaceId ||
            !user.buildingSpaceName
          ) {
            return;
          }

          const params = new URLSearchParams();
          params.append("buildingId", user.buildingId);
          params.append("buildingFloorId", user.buildingFloorId);
          params.append("floorName", user.buildingFloorName);
          params.append("buildingSpaceId", user.buildingSpaceId);
          params.append("spaceName", user.buildingSpaceName);
          navigate(`/service/hot-cold-vote/detail?${params?.toString()}`);
        }}
        user={user}
        voteSpace={voteSpace}
      />

      <HotColdVoteOnGoingPopup
        popup={popup}
        onClose={() => setPopup("")}
        onMove={() => {
          const params = new URLSearchParams();
          voteSpace?.vote?.voteId &&
            params.append("voteId", voteSpace.vote.voteId.toString());

          navigate(`/service/hot-cold-vote/detail?${params?.toString()}`);
        }}
        user={user}
        voteSpace={voteSpace}
      />

      <HotColdVoteReportPopup
        popup={popup}
        nickname={user.nickname}
        onClose={() => setPopup("")}
      />

      <CommonPopup
        isOpen={popup === "nullSpace"}
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="flex flex-col gap-2 p-7 leading-[30px] text-lg font-medium break-keep whitespace-pre-line">
          {`등록된 상세공간이 없습니다.\n건물 위치 변경 화면으로 이동하시겠습니까?`}
        </div>

        <div className="flex">
          <CommonButton
            variant="secondary"
            className="rounded-none flex-1 h-bottom-button"
            onClick={() => setPopup("")}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button w-full"
            onClick={() =>
              navigate("/myinfo/change/building", { state: { goBack: true } })
            }
          >
            이동
          </CommonButton>
        </div>
      </CommonPopup>

      {simplePopupComponent}
    </div>
  );
}

export type HotColdStatusBadgeProps = {
  vote: Vote;
};

export const HotColdStatusBadge = ({ vote }: HotColdStatusBadgeProps) => {
  const timePassed = isDateTimePassed(vote.endedAt);
  const [isEnded, setIsEnded] = useState<boolean>(timePassed);

  return (
    <div
      className={clsx(
        "flex justify-center items-center border rounded-[10px] px-2.5 py-[7px] min-h-[80px] w-[120px] cursor-pointer gap-1 break-words text-center",
        {
          "bg-brand-neutral-05 text-brand-neutral-50":
            vote.status === VOTE_STATUS.COMPLETED || isEnded,
        }
      )}
      data-testid="hot-cold-vote-badge"
    >
      {vote.status === VOTE_STATUS.ONGOING && !isEnded && (
        <div className="flex flex-col gap-0.5">
          <div className="flex justify-center items-center">
            <VoteIcon />
            <div className="flex flex-col w-[64px] h-[41px] justify-center items-center">
              <span className="text-[18px] leading-[21px] text-brand-primary-magenta font-medium">
                진행중
              </span>
              {vote.userVoteItem && (
                <span className="text-[#21212199] text-xs leading-[20px]">
                  투표 완료
                </span>
              )}
            </div>
          </div>

          <div
            className="flex justify-center items-center gap-1.5 px-2.5 h-[24px] rounded-br-[6px] rounded-bl-[6px]"
            style={{ background: "rgba(152, 171, 186, 0.1)" }}
          >
            <HotColdVoteRemainTime
              status={vote.status}
              endTime={vote.endedAt}
              prefix={<TimerIcon />}
              onEnd={() => setIsEnded(true)}
              onlyNumber
            />
          </div>
        </div>
      )}
      {(vote.status === VOTE_STATUS.COMPLETED || isEnded) && (
        <span className="text-brand-neutral-50 text-[18px]">종료</span>
      )}
    </div>
  );
};

export const VoteIcon = ({
  showBackground = false,
}: {
  showBackground?: boolean;
}) => {
  return (
    <div
      className={clsx({
        "rounded-[10px] px-[7px] py-[6px] bg-[#F4F6F9]": showBackground,
      })}
    >
      <svg
        width="30"
        height="33"
        viewBox="0 0 30 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="6.96094"
          y="19.7139"
          width="12.9905"
          height="8.78238"
          transform="rotate(-45 6.96094 19.7139)"
          fill="white"
          stroke="black"
          strokeWidth="1.5"
        />
        <circle
          cx="14.8389"
          cy="18.0752"
          r="2.87611"
          transform="rotate(-45 14.8389 18.0752)"
          fill="#E719B7"
        />
        <path
          d="M13.3943 18.9958L15.2577 18.9958L15.2577 16.129"
          stroke="white"
          strokeLinejoin="round"
        />
        <path
          d="M12.2763 7.27758L17.018 3.9361L17.3538 3.6748C18.7974 4.96907 21.6249 7.62479 21.3859 7.8936C21.0873 8.22961 18.6605 11.8884 17.9698 13.3818C17.2792 14.8751 15.5431 15.2672 14.6844 14.6885C13.9975 14.2255 15.1324 12.0564 15.7858 11.0297L11.3802 15.2672C9.66277 15.4912 10.671 9.77899 10.9323 8.88296C11.1414 8.16614 11.9154 7.51403 12.2763 7.27758Z"
          fill="white"
          stroke="black"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <rect
          x="18.5859"
          y="1.04297"
          width="7.57674"
          height="2.97066"
          rx="0.5"
          transform="rotate(45 18.5859 1.04297)"
          fill="white"
          stroke="black"
          strokeWidth="1.5"
        />
        <rect
          x="3"
          y="23.3887"
          width="24"
          height="8"
          fill="white"
          stroke="black"
          strokeWidth="1.5"
        />
        <rect
          x="1"
          y="22.8975"
          width="28"
          height="3.5"
          fill="white"
          stroke="black"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
};

export const TimerIcon = () => {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.16592 2.03846C6.16592 1.74108 6.40699 1.5 6.70438 1.5H10.2941C10.5915 1.5 10.8326 1.74108 10.8326 2.03846C10.8326 2.33585 10.5915 2.57692 10.2941 2.57692H6.70438C6.40699 2.57692 6.16592 2.33585 6.16592 2.03846ZM2.57617 9.44769C2.57617 6.1172 5.21606 3.39538 8.49925 3.39538C11.7824 3.39538 14.4223 6.1172 14.4223 9.44769C14.4223 12.7782 11.7824 15.5 8.49925 15.5C5.21606 15.5 2.57617 12.7782 2.57617 9.44769ZM8.49925 5.55144C8.79663 5.55144 9.03771 5.79252 9.03771 6.0899V9.03999C9.03771 9.139 9.08179 9.22884 9.15138 9.28585L10.9115 10.7277C11.1415 10.9162 11.1752 11.2554 10.9868 11.4855C10.7983 11.7155 10.4591 11.7493 10.229 11.5608L8.46892 10.1189C8.14549 9.85397 7.96079 9.45636 7.96079 9.03999V6.0899C7.96079 5.79252 8.20186 5.55144 8.49925 5.55144Z"
        fill="#333F48"
      />
    </svg>
  );
};
